/**
 * The widget bundle the loader fetches at runtime.
 *
 * Built as an IIFE with `globalName: MhosaicFeedback`, so once the bundle
 * is parsed by the browser, `window.MhosaicFeedback = { createFeedback }`
 * is set. The loader (`src/loader/`) injects this script with SRI and
 * picks up the global.
 *
 * Distinct from `src/embed.ts` (which is the legacy CDN script-tag entry
 * that auto-initializes from `data-key` attributes). This entry deliberately
 * does NOT auto-init — it just exposes the API for the loader to call.
 *
 * Self-arming error tracking: this bundle wraps the instance with
 * `withErrorTracking` BY DEFAULT. The bundle is the only part of a
 * loader-path install that auto-updates (the host's pinned npm shim never
 * changes), so arming here is what lets already-deployed clients start
 * filing synthetic frontend reports on their next page load — no host
 * redeploy. The loader forwards the per-project `error_tracking` manifest
 * flag as `config.errorTracking`; pass `false` to opt out (default on, the
 * platform's chosen posture). `withErrorTracking` is idempotent, so a host
 * that ALSO wraps via <FeedbackProvider> won't double-register.
 */

import { createFeedback as baseCreateFeedback, type InternalConfig } from './core'
import { withErrorTracking, type ErrorTrackingOptions } from './modules/error-tracking'

export interface WidgetConfig extends InternalConfig {
  /** Off-switch for the bundle's default error capture. Default on. */
  errorTracking?: boolean | ErrorTrackingOptions
}

export function createFeedback(config: WidgetConfig) {
  const { errorTracking, ...base } = config
  const fb = baseCreateFeedback(base)
  if (errorTracking !== false) {
    withErrorTracking(fb, typeof errorTracking === 'object' ? errorTracking : undefined)
  }
  return fb
}

export type {
  FeedbackApi,
  FeedbackConfig,
  FeedbackEnv,
  FeedbackSeverity,
  FeedbackType,
  ReportPayload,
  SubmittedReport,
  UserIdentity,
} from './types'
